import * as Sentry from "@sentry/browser";

class GtmEvent {
  constructor(dataLayer, currency='USD') {
    this.dataLayer = dataLayer
    this.currency = currency
    if(!this.dataLayer) {
      Sentry.captureException('GTM not found');
    }
  }

  cartEventInfo(cartData) {
    return {
      currency: this.currency,
      value: cartData.finalPrice,
      items: cartData.items.map((item, index) => ({
          item_id: item.id,
          item_name: item.title,
          item_brand: item.designer,
          price: item.price,
          quantity: 1,
        })
      )
    }
  }

  postEventInfo(postData) {
    return {
      currency: this.currency,
      value: postData.price,
      items: [
        {
          item_id: postData.id,
          item_name: postData.title,
          currency: this.currency,
          index: 0,
          item_brand: postData.creator?.brand_name,
          price: postData.price,
          quantity: 1
        }
      ]
    }
  }

  downloadEventInfo(downloadData) {
    if(downloadData) {
      downloadData = {
        post_id: downloadData.id,
        post_title: downloadData.title,
      }
    } else {
      downloadData = {}
    }
    return {
      ...downloadData,
    }
  }

  userEventInfo(userData) {
    if(userData) {
      userData = {
        utm_source: userData.utm_source,
      }
    } else {
      userData = {}
    }
    return {
      ...userData,
    }
  }

  // ga4 ecommerce event list

  // normal
  // 1. view_item         - postEventInfo 
  // 2. add_to_cart       - postEventInfo
  // 3. remove_from_cart  - postEventInfo
  // 4. add_to_wishlist   - postEventInfo
  
  // checkout start
  // 5. view_cart         - cartEventInfo
  // 6. begin_checkout    
  // 7. add_shipping_info
  // 8. add_payment_info
  // 9. purchase          - puchaseInfo

  // 10. refund
  // 11. view_item_list
  // 12. select_item

  postEvent(event, postData) {
    try{
      if(postData) {
        this.dataLayer.push({ ecommerce: null });
        this.dataLayer.push({
          event,
          ecommerce: {
            ...this.postEventInfo(postData),
          }
        })
      }
    } catch(err) {
      Sentry.captureException(err);
    }
  }

  cartEvent(event, cartData) {
    try{
      if(cartData) {
        this.dataLayer.push({ ecommerce: null });
        this.dataLayer.push({
          event,
          ecommerce: {
            ...this.cartEventInfo(cartData),
          }
        })
      }
    } catch(err) {
      Sentry.captureException(err);
    }
  }

  trackDownload(event, downloadData) {
    try{
      if(downloadData) {
        this.dataLayer.push({ params: null });
        this.dataLayer.push({
          event,
          params: {
            ...this.downloadEventInfo(downloadData),
          }
        })
      }
    } catch(err) {
      Sentry.captureException(err);
    }
  }

  userEvent(event, userData) {
    try{
      if(userData) {
        this.dataLayer.push({ params: null });
        this.dataLayer.push({
          event,
          params: {
            ...this.userEventInfo(userData),
          }
        })
      }
    } catch(err) {
      Sentry.captureException(err);
    }
  }

  share(shareData) {
    try{
      if(shareData) {
        this.dataLayer.push({ params: null });
        this.dataLayer.push({
          event: 'share',
          params: {
            method: shareData.method,
            content_type: shareData.content_type,
            item_id: shareData.item_id,
          }
        })
      }
    } catch(err) {
      Sentry.captureException(err);
    }
  }

  purchase(orderData) {
    try{
      if(orderData) {
        this.dataLayer.push({ ecommerce: null });
        this.dataLayer.push({
          event: 'purchase',
          ecommerce: {
            transaction_id: orderData.transaction_id,
            value: orderData.order_price,
            tax: orderData.tax,
            shipping: orderData.shipping,
            currency: orderData.currency,
            items: orderData.items.map((item, index) => ({
              item_id: item.post_id,
              item_name: item.post_title,
              item_brand: item.creator_name,
              item_category: item.post_category,
              item_variant: item.variant_title,
              price: item.variant_price,
              quantity: item.quantity
            }))
          }
        })
      }
    } catch(err) {
      Sentry.captureException(err);
    }
  }

  // custom events (non-ecommerce)

  search(searchData) {
    try{
      if(searchData) {
        this.dataLayer.push({ params: null });
        this.dataLayer.push({
          event: 'search',
          params: {
            search_term: searchData.search_term,
          }
        })
      }
    } catch(err) {
      Sentry.captureException(err);
    }
  }

  viewItemList(listData) {
    try{
      if(listData) {
        this.dataLayer.push({ ecommerce: null });
        this.dataLayer.push({
          event: 'view_item_list',
          ecommerce: {
            item_list_id: listData.item_list_id,
            item_list_name: listData.item_list_name,
            currency: this.currency,
            ...(typeof listData.item_list_index === 'number' ? { item_list_index: listData.item_list_index } : {}),
            items: listData.items.map((item, index) => ({
              item_id: item.id,
              item_name: item.title,
              item_brand: item.creator,
              price: item.price,
              index: typeof item.index === 'number' ? item.index : index,
            }))
          }
        })
      }
    } catch(err) {
      Sentry.captureException(err);
    }
  }

  selectItem(selectData) {
    try{
      if(selectData) {
        this.dataLayer.push({ ecommerce: null });
        this.dataLayer.push({
          event: 'select_item',
          ecommerce: {
            item_list_id: selectData.item_list_id,
            item_list_name: selectData.item_list_name,
            items: [{
              item_id: selectData.item_id,
              item_name: selectData.item_name,
              item_brand: selectData.item_brand,
              price: selectData.price,
              index: selectData.index,
            }]
          }
        })
      }
    } catch(err) {
      Sentry.captureException(err);
    }
  }

  applyCoupon(couponData) {
    try{
      if(couponData) {
        this.dataLayer.push({ params: null });
        this.dataLayer.push({
          event: 'apply_coupon',
          params: {
            coupon_type: couponData.coupon_type,
            coupon_code: couponData.coupon_code,
            discount_value: couponData.discount_value,
          }
        })
      }
    } catch(err) {
      Sentry.captureException(err);
    }
  }

  likePost(data) {
    try{
      if(data) {
        this.dataLayer.push({ params: null });
        this.dataLayer.push({
          event: 'like_post',
          params: {
            post_id: data.post_id,
            post_name: data.post_name,
          }
        })
      }
    } catch(err) {
      Sentry.captureException(err);
    }
  }

  followCreator(data) {
    try{
      if(data) {
        this.dataLayer.push({ params: null });
        this.dataLayer.push({
          event: 'follow_creator',
          params: {
            creator_name: data.creator_name,
            action: data.action,
          }
        })
      }
    } catch(err) {
      Sentry.captureException(err);
    }
  }

  viewSerie(data) {
    try{
      if(data) {
        this.dataLayer.push({ params: null });
        this.dataLayer.push({
          event: 'view_serie',
          params: {
            serie_id: data.serie_id,
            serie_name: data.serie_name,
          }
        })
      }
    } catch(err) {
      Sentry.captureException(err);
    }
  }

  clickBanner(data) {
    try{
      if(data) {
        this.dataLayer.push({ params: null });
        this.dataLayer.push({
          event: 'click_banner',
          params: {
            banner_id: data.banner_id,
            banner_name: data.banner_name,
            banner_slot: data.banner_slot,
            banner_link: data.banner_link,
          }
        })
      }
    } catch(err) {
      Sentry.captureException(err);
    }
  }

  // unified module impression/click schema
  // page_type: 頁面類型 (series / blog / home / designer / post ...)
  // module: 模組識別 (post_grid / model_reco / weekly_drop / similar / same_creator ...)
  // landing_path: 使用者進站頁面路徑
  // target_type: 被看到/點擊的目標類型 (post / profile / series / banner / follow_button / like_button ...)
  // target_id: 目標 id
  // position: 在模組內的位置（例如第幾張卡片）

  moduleImpression(data) {
    this.moduleEvent('module_impression', data)
  }

  moduleClick(data) {
    this.moduleEvent('module_click', data)
  }

  moduleEvent(event, data) {
    try{
      if(data) {
        if (!data.target_type || data.target_id == null) {
          console.warn(`[GtmEvent] ${event} missing target_type/target_id`, data);
        }
        this.dataLayer.push({
          event,
          params: {
            page_type: data.page_type,
            module: data.module,
            landing_path: data.landing_path,
            target_type: data.target_type,
            target_id: data.target_id,
            position: data.position,
            milestone_pct: data.milestone_pct,
            action: data.action,
          }
        })
      }
    } catch(err) {
      Sentry.captureException(err);
    }
  }

  // example
  // gtag('consent', 'update', {
  //   'ad_storage': 'granted'
  // });
  setConsent() {
    try{
      if(arguments) {
        this.dataLayer.push(arguments)
      }
    } catch(err) {
      Sentry.captureException(err);
    }
  }
}
export default GtmEvent;
 
